import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  FitnessCenter as FitnessCenterIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';

const EmailVerifiedPage = () => {
  const { t } = useTranslation('emailVerification');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Get email and token from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    const tokenParam = urlParams.get('token');
    const verifiedParam = urlParams.get('verified');

    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }

    if (tokenParam) {
      setToken(tokenParam);
    }

    // If not coming from successful verification, redirect
    if (!verifiedParam || verifiedParam !== 'true') {
      navigate('/email-verification');
      return;
    }

    // Hide confetti after 5 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [location.search, navigate]);

  const handleContinueRegistration = () => {
    const registrationUrl = `/register?email=${encodeURIComponent(email)}&token=${token}&verified=true`;
    navigate(registrationUrl);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          gravity={0.1}
        />
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, type: 'spring', bounce: 0.4 }}
      >
        <Paper
          elevation={8}
          sx={{
            p: isMobile ? 3 : 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(255,255,255,0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(76, 175, 80, 0.3)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Box position="relative" display="inline-block">
                <CheckCircleIcon
                  sx={{
                    fontSize: 96,
                    color: 'success.main',
                    mb: 2
                  }}
                />
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: 96,
                      color: 'success.main',
                      opacity: 0.3
                    }}
                  />
                </motion.div>
              </Box>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="success.main">
                {t('verified.title')}
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                {t('verified.subtitle')}
              </Typography>
            </motion.div>
          </Box>

          {/* Success Message */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Alert severity="success" sx={{ mb: 4, fontSize: '1.1rem' }}>
              {t('verified.message')}
            </Alert>
          </motion.div>

          {/* Email Confirmation */}
          <Box mb={4} textAlign="center">
            <Typography variant="body1" color="text.secondary" mb={2}>
              {t('verified.info.emailConfirmed', { email })}
            </Typography>

            <Chip
              icon={<EmailIcon />}
              label={email}
              color="success"
              variant="filled"
              sx={{ fontSize: '1rem', py: 2, px: 1 }}
            />
          </Box>

          {/* Next Steps Info */}
          <Card variant="outlined" sx={{ mb: 4, borderColor: 'success.main', borderWidth: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon color="warning" />
                {t('verified.info.nextSteps')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonAddIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('verified.nextSteps.completeProfile')}
                    secondary={t('verified.nextSteps.profileDescription')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <FitnessCenterIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('verified.nextSteps.selectPlan')}
                    secondary={t('verified.nextSteps.planDescription')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('verified.nextSteps.bookFacilities')}
                    secondary={t('verified.nextSteps.facilitiesDescription')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <GroupIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('verified.nextSteps.joinClasses')}
                    secondary={t('verified.nextSteps.classesDescription')}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Actions */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={handleContinueRegistration}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(45deg, #4CAF50, #66BB6A)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #45a049, #5cb85c)',
                  }
                }}
              >
                {t('verified.actions.continueRegistration')}
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<LoginIcon />}
                onClick={handleBackToLogin}
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                {t('verified.actions.backToLogin')}
              </Button>
            </Box>
          </motion.div>

          {/* Footer */}
          <Divider sx={{ my: 3 }} />
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {t('verified.footer')}
            </Typography>
          </Box>
        </Paper>
      </motion.div>

      {/* Additional Success Animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: -1
        }}
      >
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [-10, -30, -10],
              x: [0, Math.random() * 20 - 10, 0],
              rotate: [0, 360],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'easeInOut'
            }}
            style={{
              position: 'absolute',
              left: `${10 + i * 15}%`,
              top: `${20 + Math.random() * 60}%`,
              fontSize: '2rem'
            }}
          >
            ⭐
          </motion.div>
        ))}
      </motion.div>
    </Container>
  );
};

export default EmailVerifiedPage;